import { useCallback, useEffect, useMemo, useState } from 'react'
import { BranchesOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import './VersionChangelog.css'

type EntryType = 'added' | 'fixed' | 'changed' | 'removed' | 'docs'
type Entry = { hash: string; date: string; type: EntryType; scope: string | null; message: string; translations?: Record<string, string> }
type Group = { version: string; released_at: string | null; entries: Entry[] }
type Data = { currentVersion: string; total: number; versions: Group[] }
type Grouping = 'version' | 'date'
const displayVersion = (version?: string | null) => version ? version.split('-')[0] : '…'
const LABELS: Record<EntryType, string> = { added: 'Added', fixed: 'Fixed', changed: 'Changed', removed: 'Removed', docs: 'Docs' }
const TYPES = Object.keys(LABELS) as EntryType[]

export default function VersionChangelog() {
  const { i18n } = useTranslation()
  const [data, setData] = useState<Data | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [grouping, setGrouping] = useState<Grouping>('version')
  const [filter, setFilter] = useState<EntryType | 'all'>('all')
  const storageKey = 'changelog:last-read'
  const load = useCallback(() => {
    setLoading(true)
    const base = import.meta.env.BASE_URL || '/'
    fetch(`${base}changelog.json`, { cache: 'no-store' })
      .then((response) => { if (!response.ok) throw new Error(String(response.status)); return response.json() as Promise<Data> })
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false))
  }, [])
  useEffect(load, [load])
  useEffect(() => {
    if (!open) return
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [open])
  const allEntries = useMemo(() => data?.versions.flatMap((group) => group.entries) ?? [], [data])
  const counts = useMemo(() => Object.fromEntries(TYPES.map((type) => [type, allEntries.filter((entry) => entry.type === type).length])), [allEntries])
  const groups = useMemo(() => {
    if (!data) return []
    const source = grouping === 'version' ? data.versions : Array.from(allEntries.reduce((map, entry) => {
      const current = map.get(entry.date) ?? []; current.push(entry); map.set(entry.date, current); return map
    }, new Map<string, Entry[]>())).map(([date, entries]) => ({ version: date, released_at: date, entries }))
    return source.map((group) => ({ ...group, entries: group.entries.filter((entry) => filter === 'all' || entry.type === filter) }))
      .filter((group) => group.entries.length > 0)
  }, [allEntries, data, filter, grouping])
  const unread = Boolean(data?.currentVersion && localStorage.getItem(storageKey) !== data.currentVersion)
  const show = () => { setOpen(true); if (data?.currentVersion) localStorage.setItem(storageKey, data.currentVersion) }
  return <>
    <button type="button" className="vc-trigger" onClick={show} title="История изменений">
      <BranchesOutlined /><span>{displayVersion(data?.currentVersion)}</span>{unread && <span className="vc-unread" />}
    </button>
    {open && <div className="vc-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false) }}>
      <section className="vc-modal" role="dialog" aria-modal="true" aria-labelledby="vc-title">
        <header className="vc-header">
          <div className="vc-mark"><BranchesOutlined /></div>
          <div className="vc-heading"><h2 id="vc-title">История изменений</h2><p>{data?.total ?? 0} обновлений</p></div>
          <button type="button" className="vc-icon-button" onClick={load} aria-label="Обновить"><ReloadOutlined className={loading ? 'vc-spin' : ''} /></button>
          <button type="button" className="vc-icon-button" onClick={() => setOpen(false)} aria-label="Закрыть"><CloseOutlined /></button>
        </header>
        <div className="vc-grouping"><span>Группировать</span><div className="vc-segmented">
          <button type="button" className={grouping === 'version' ? 'active' : ''} onClick={() => setGrouping('version')}>Версия</button>
          <button type="button" className={grouping === 'date' ? 'active' : ''} onClick={() => setGrouping('date')}>Дата</button>
        </div></div>
        <div className="vc-filters" aria-label="Фильтр изменений">
          <button type="button" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Все ({allEntries.length})</button>
          {TYPES.filter((type) => (counts[type] ?? 0) > 0).map((type) => <button type="button" key={type} className={filter === type ? 'active' : ''} onClick={() => setFilter(type)}>{LABELS[type]} ({counts[type]})</button>)}
        </div>
        <div className="vc-content">
          {loading ? <div className="vc-state">Загрузка…</div> : groups.length === 0 ? <div className="vc-state">Изменений пока нет</div> : groups.map((group) => <section className="vc-release" key={group.version}>
            <div className="vc-release-title"><span /><strong>{displayVersion(group.version)}</strong><span /></div>
            {group.entries.map((entry) => <article className="vc-entry" key={`${group.version}-${entry.hash}`}>
              <span className={`vc-type vc-type-${entry.type}`}>{LABELS[entry.type]}</span>
              <p>{entry.scope && <strong>[{entry.scope}] </strong>}{entry.translations?.[(i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] || 'en'] || entry.translations?.en || entry.message}</p>
            </article>)}
          </section>)}
        </div>
        <footer className="vc-footer"><span>Показано {groups.reduce((sum, group) => sum + group.entries.length, 0)} из {allEntries.length}</span><button type="button" onClick={() => setOpen(false)}>Закрыть</button></footer>
      </section>
    </div>}
  </>
}
